import axios from "axios";
import { useState, useEffect, useCallback, useId } from "react";
import { Modal } from "react-bootstrap";

function CustomExtraField({ field, fieldId }) {
  const isRequired = Boolean(field.required);
  const placeholder = field.placeholder || "";
  const helpText = field.help_text || "";
  const initial = field.initial;

  if (field.type === "textarea") {
    return (
      <div className="mb-3">
        <label className="form-label fw-bold" htmlFor={fieldId}>
          {field.label}
          {isRequired && <span className="text-danger"> *</span>}
        </label>
        <textarea
          className="form-control"
          id={fieldId}
          name={field.name}
          required={isRequired}
          defaultValue={typeof initial === "string" ? initial : ""}
          placeholder={placeholder}
        />
        {helpText && <small className="text-muted d-block mt-1">{helpText}</small>}
      </div>
    );
  }

  if (field.type === "select" || field.type === "multiselect") {
    const multiple = field.type === "multiselect";
    return (
      <div className="mb-3">
        <label className="form-label fw-bold" htmlFor={fieldId}>
          {field.label}
          {isRequired && <span className="text-danger"> *</span>}
        </label>
        <select
          className="form-select"
          id={fieldId}
          name={field.name}
          required={isRequired}
          multiple={multiple}
          defaultValue={
            multiple
              ? Array.isArray(initial)
                ? initial
                : []
              : typeof initial === "string" || typeof initial === "number"
                ? String(initial)
                : ""
          }
        >
          {!multiple && <option value="">---</option>}
          {(field.choices || []).map((choice) => (
            <option key={`${field.name}-${choice.value}`} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
        {helpText && <small className="text-muted d-block mt-1">{helpText}</small>}
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id={fieldId}
            name={field.name}
            required={isRequired}
            defaultChecked={Boolean(initial)}
          />
          <label className="form-check-label" htmlFor={fieldId}>
            {field.label}
            {isRequired && <span className="text-danger"> *</span>}
          </label>
        </div>
        {helpText && <small className="text-muted d-block mt-1">{helpText}</small>}
      </div>
    );
  }

  return (
    <div className="mb-3">
      <label className="form-label fw-bold" htmlFor={fieldId}>
        {field.label}
        {isRequired && <span className="text-danger"> *</span>}
      </label>
      <input
        className="form-control"
        id={fieldId}
        name={field.name}
        type={field.type || "text"}
        required={isRequired}
        defaultValue={
          typeof initial === "string" || typeof initial === "number"
            ? String(initial)
            : ""
        }
        placeholder={placeholder}
      />
      {helpText && <small className="text-muted d-block mt-1">{helpText}</small>}
    </div>
  );
}

function SuggestionForm({ onHide, show, config, embedded = false }) {
  const [postLoading, setPostLoading] = useState(false);
  const [postSucess, setPostSucess] = useState(false);
  const [postError, setPostError] = useState(false);
  const fieldIdPrefix = useId();

  const isAnonymous = config.profile === "";
  const captchaEnabled =
    isAnonymous &&
    (config.anonymous_captcha_enabled || "").toLowerCase() === "true";
  const anonymousExtraFields = Array.isArray(config.anonymous_extra_fields)
    ? config.anonymous_extra_fields
    : [];
  const apiUrl = isAnonymous
    ? "/api/v1/suggestions-anonymous/"
    : "/api/v1/suggestions/";

  const [captchaKey, setCaptchaKey] = useState("");
  const [captchaImageUrl, setCaptchaImageUrl] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");

  const loadCaptcha = useCallback(async () => {
    if (!captchaEnabled) return;
    try {
      const res = await axios.get("/captcha/refresh/", {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      setCaptchaKey(res.data.key);
      setCaptchaImageUrl(res.data.image_url);
      setCaptchaValue("");
    } catch (err) {
      console.error("Error loading captcha", err);
    }
  }, [captchaEnabled]);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    if (isAnonymous && anonymousExtraFields.length > 0) {
      const extraData = {};

      for (const field of anonymousExtraFields) {
        if (field.type === "checkbox") {
          extraData[field.name] = formData.has(field.name);
        } else if (field.type === "multiselect") {
          extraData[field.name] = formData.getAll(field.name);
        } else {
          extraData[field.name] = formData.get(field.name) || "";
        }

        formData.delete(field.name);
      }

      formData.set("extra_data", JSON.stringify(extraData));
    }

    if (captchaEnabled) {
      formData.set("captcha_key", captchaKey);
      formData.set("captcha_value", captchaValue);
    }

    setPostLoading(true);

    try {
      await axios.post(apiUrl, formData, {
        withCredentials: true,
        xsrfCookieName: "csrftoken",
        xsrfHeaderName: "X-CSRFToken",
      });

      setPostLoading(false);
      setPostSucess(true);
      setPostError(false);
      e.target.reset();
    } catch (error) {
      setPostLoading(false);
      setPostError(true);
      if (captchaEnabled) {
        loadCaptcha();
      }
    }
  };

  const formContents = (
    <form onSubmit={handleSubmit}>
      {isAnonymous && (
        <>
          <label className="form-label fw-bold" htmlFor={`${fieldIdPrefix}-email`}>
            Email
          </label>
          <input
            className="form-control mb-3"
            name="email"
            defaultValue=""
            type="email"
            id={`${fieldIdPrefix}-email`}
            required
            placeholder="email@ejemplo.com"
          />
        </>
      )}

      <label className="form-label fw-bold" htmlFor={`${fieldIdPrefix}-title`}>
        {config.translations.suggestionFormLabelTitle}
      </label>
      <input
        className="form-control mb-3"
        name="title"
        defaultValue=""
        type="text"
        id={`${fieldIdPrefix}-title`}
        required
        placeholder={config.translations.suggestionFormLabelTitlePH}
      />

      <label className="form-label fw-bold" htmlFor={`${fieldIdPrefix}-description`}>
        {config.translations.suggestionFormLabelBody}
      </label>
      <textarea
        style={{ minHeight: "150px" }}
        className="form-control"
        name="description"
        defaultValue=""
        id={`${fieldIdPrefix}-description`}
        placeholder={config.translations.suggestionFormLabelBodyPH}
        required
      ></textarea>

      {isAnonymous &&
        anonymousExtraFields.map((field) => (
          <CustomExtraField
            key={field.name}
            field={field}
            fieldId={`${fieldIdPrefix}-${field.name}`}
          />
        ))}

      {isAnonymous && (
        <div className="mt-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              name="policy_accepted"
              id={`${fieldIdPrefix}-policy_accepted`}
              required
            />
            <label className="form-check-label" htmlFor={`${fieldIdPrefix}-policy_accepted`}>
              {config.translations.suggestionFormLabelPolicyAccepted}{" "}
              <span className="text-danger">*</span>
            </label>
          </div>
        </div>
      )}

      {captchaEnabled && captchaImageUrl && (
        <div className="mt-3">
          <label className="form-label fw-bold">Captcha</label>
          <div className="d-flex align-items-center gap-2 mb-2">
            <img
              src={captchaImageUrl}
              alt="captcha"
              style={{ height: "40px", cursor: "pointer" }}
              onClick={loadCaptcha}
              title="Click para refrescar"
            />

            <input
              className="form-control"
              type="text"
              value={captchaValue}
              onChange={(event) => setCaptchaValue(event.target.value)}
              required
              placeholder={config.translations.suggestionCaptcha}
            />

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={loadCaptcha}
            >
              <i className="fa-solid fa-arrows-rotate"></i>
            </button>
          </div>
        </div>
      )}

      {postError && (
        <p className="text-danger">{config.translations.suggestionFormError}</p>
      )}

      <div className="center mt-4">
        <button
          className="btn btn-primary center gap-2"
          type="submit"
          disabled={postLoading}
        >
          {postLoading ? (
            <>
              <span
                className="spinner-border text-light spinner-border-sm"
                aria-hidden="true"
              ></span>
              <span role="status">{config.translations.suggestionLoading}</span>
            </>
          ) : (
            <>{config.translations.suggestionFormBtn}</>
          )}
        </button>
      </div>
    </form>
  );

  const successContents = (
    <div className="horizontal-center flex-column gap-4">
      <i
        className="fa-solid fa-circle-check text-success"
        style={{ fontSize: "4rem" }}
      ></i>
      <h4 className="fs-4 text-center">
        {config.translations.suggestionFormSucess}
      </h4>
      {!embedded && (
        <button
          className="btn btn-primary"
          type="button"
          onClick={onHide}
        >
          {config.translations.suggestionFormSucessBtn}
        </button>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div className="card p-4 shadow-sm w-100">
        <h3 className="h4 mb-3">{config.translations.suggestionFormTitle}</h3>
        {!postSucess ? formContents : successContents}
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onHide}>
      <div>
        <Modal.Header>
          <Modal.Title className="SuggestionForm-title">
            {config.translations.suggestionFormTitle}
          </Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onHide}
          ></button>
        </Modal.Header>
        <Modal.Body>{!postSucess ? formContents : successContents}</Modal.Body>
      </div>
    </Modal>
  );
}

export default SuggestionForm;
